"use client";
import {
  Box,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  Avatar,
  Divider,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { NavLink, useLocation } from "react-router-dom";
import { NavbarOptions, getFilteredModuleOptions } from "./widget/constants";
import LoginIcon from "@mui/icons-material/Login";
import {
  mobileMenuStyles,
  getPaperStyles,
  getOptionTextStyles,
} from "./widget/mobileMenuStyle";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store/store";

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
  selectedModule: string;
}

const MobileMenu = ({ open, onClose, selectedModule }: MobileMenuProps) => {
  const menuOptions = NavbarOptions[selectedModule] || [];
  const isMobile = useMediaQuery("(max-width: 600px)");
  const profile = useSelector((state: RootState) => state.auth.user);
  const userPermissions = useSelector(
    (state: RootState) => state.auth.permissions,
  );

  const filteredMenuOptions = getFilteredModuleOptions(
    menuOptions?.options || [],
    userPermissions,
    profile,
  );

  const buildFullPath = (optionPath: string) => {
    const basePath = NavbarOptions[selectedModule]?.basePath;
    if (!basePath) return `/${optionPath}`;

    if (selectedModule === "BROKER_PORTAL" && profile?.uuid) {
      return `/${basePath}/agent/${profile.uuid}/${optionPath}`;
    }
    if (selectedModule === "EMPLOYER_PORTAL" && profile?.employerUuid) {
      return `/${basePath}/${profile.employerUuid}/${optionPath}`;
    }
    if (selectedModule === "AFFILIATE_PORTAL" && profile?.affiliateGroupUuid) {
      return `/${basePath}/${profile.affiliateGroupUuid}/${optionPath}`;
    }
    return `/${basePath}/${optionPath}`;
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: getPaperStyles(isMobile),
      }}
    >
      <Box sx={mobileMenuStyles.header}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="h6" sx={mobileMenuStyles.title}>
            {NavbarOptions[selectedModule]?.title || ""}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={mobileMenuStyles.closeButton}>
          <CloseIcon />
        </IconButton>
      </Box>

      <List>
        {filteredMenuOptions.map((option) => (
          <ListItem key={option.path} disablePadding>
            <ListItemButton
              component={NavLink}
              to={buildFullPath(option.path)}
              onClick={onClose}
              sx={{
                py: 1,
                "&.active": {
                  backgroundColor: "#E5E9EB",
                  borderRadius: "8px",
                },
                "&:hover": {
                  backgroundColor: "#F2F4F5",
                  borderRadius: "8px",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: isMobile ? "16px" : "18px",
                  fontWeight: (props: any) =>
                    props.className?.includes("active") ? 600 : 400,
                }}
              >
                {option.name}
              </Typography>
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Box sx={mobileMenuStyles.footer}>
        <Divider sx={mobileMenuStyles.divider} />
        <Box sx={mobileMenuStyles.userContainer}>
          <Box sx={mobileMenuStyles.userInfo}>
            <Avatar
              sx={mobileMenuStyles.avatar}
              alt={`${profile?.firstName}`}
            />
            <Box>
              <Typography sx={mobileMenuStyles.userName}>
                {profile?.firstName || profile?.lastName
                  ? `${profile?.firstName || ""} ${profile?.lastName || ""}`.trim()
                  : "Unknown User"}
              </Typography>
              <Typography sx={mobileMenuStyles.userEmail}>
                {profile?.email ? profile?.email : "NA"}
              </Typography>
            </Box>
          </Box>
          <Box sx={mobileMenuStyles.logout}>
            <LoginIcon sx={mobileMenuStyles.logoutIcon} />
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default MobileMenu;
